import { useState } from "react";
import { subMonths, format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useClassTrialStats } from "@/hooks/useClassTrialStats";
import { TrialStatsSummaryCards } from "@/components/admin/class-trial-stats/TrialStatsSummaryCards";
import { TrialStatsCharts } from "@/components/admin/class-trial-stats/TrialStatsCharts";
import { ConversionTimeDashboard } from "@/components/admin/class-trial-stats/ConversionTimeDashboard";
import { TeacherStatsTable } from "@/components/admin/class-trial-stats/TeacherStatsTable";
import { BranchStatsCards } from "@/components/admin/class-trial-stats/BranchStatsCards";
import { TrialStatsCSVExport } from "@/components/admin/class-trial-stats/TrialStatsCSVExport";

const AdminClassTrialStatsPage = () => {
  const [dateFrom, setDateFrom] = useState<Date>(subMonths(new Date(), 6));
  const [dateTo, setDateTo] = useState<Date>(new Date());
  const [branch, setBranch] = useState("ทุกสาขา");

  const { data, isLoading } = useClassTrialStats(dateFrom, dateTo, branch);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AdminSidebar />
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="text-xl font-bold">สถิติทดลองเรียน</h1>
              <div className="flex flex-wrap items-center gap-2">
                {/* Date From */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className={cn("w-[140px] justify-start text-left", !dateFrom && "text-muted-foreground")}>
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {format(dateFrom, "dd/MM/yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateFrom} onSelect={(d) => d && setDateFrom(d)} className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground text-sm">ถึง</span>
                {/* Date To */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className={cn("w-[140px] justify-start text-left", !dateTo && "text-muted-foreground")}>
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {format(dateTo, "dd/MM/yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateTo} onSelect={(d) => d && setDateTo(d)} className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
                {/* Branch */}
                <Select value={branch} onValueChange={setBranch}>
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ทุกสาขา">ทุกสาขา</SelectItem>
                    {(data?.branches || []).map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {data && <TrialStatsCSVExport teacherStats={data.teacherStats} branchStats={data.branchStats} />}
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-20 text-muted-foreground">กำลังโหลดข้อมูล...</div>
            ) : data ? (
              <>
                <TrialStatsSummaryCards
                  total={data.totalCount}
                  success={data.successCount}
                  failure={data.failureCount}
                  refund={data.refundCount}
                  inProgress={data.inProgressCount}
                  conversionRate={data.conversionRate}
                  decisionRate={data.decisionRate}
                />
                <TrialStatsCharts
                  success={data.successCount}
                  failure={data.failureCount}
                  refund={data.refundCount}
                  inProgress={data.inProgressCount}
                  monthlyTrends={data.monthlyTrends}
                />
                <ConversionTimeDashboard
                  totalConversions={data.conversionEntries.length}
                  avgDays={data.avgDays}
                  minDays={data.minDays}
                  maxDays={data.maxDays}
                  buckets={data.buckets}
                  teacherSpeed={data.teacherConversionSpeed}
                />
                <TeacherStatsTable teacherStats={data.teacherStats} />
                <div>
                  <h2 className="text-base font-semibold mb-3">สถิติรายสาขา</h2>
                  <BranchStatsCards branchStats={data.branchStats} />
                </div>
              </>
            ) : null}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AdminClassTrialStatsPage;
